import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUrl';
import config from '../config';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Car, Heart, MessageCircle, MapPin, Fuel, Settings, Calendar, Gauge, Pencil } from 'lucide-react';
import Navbar from '../components/Navbar';
import { detailAnnonce, ajouterFavori, envoyerMessage, contactWhatsapp } from '../services/api';
import { useAuth } from '../context/AuthContext';
import BannierePublicitaire from '../components/BannierePublicitaire';
import BadgeVerification from '../components/BadgeVerification';  

const DetailAnnonce = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [annonce, setAnnonce] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [msgEnvoye, setMsgEnvoye] = useState(false);
  const [favoriAjoute, setFavoriAjoute] = useState(false);
  const [photoActive, setPhotoActive] = useState(0);

  useEffect(() => {
    detailAnnonce(id)
      .then(res => setAnnonce(res.data))
      .catch(() => navigate('/annonces'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleFavori = async () => {
    if (!user) return navigate('/connexion');
    try {
      await ajouterFavori(id);
      setFavoriAjoute(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleMessage = async (e) => {
    e.preventDefault();
    if (!user) return navigate('/connexion');
    try {
      await envoyerMessage({
        destinataire_id: annonce.vendeur_id,
        annonce_id: annonce.id,
        contenu: message
      });
      setMsgEnvoye(true);
      setMessage('');
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50"><Navbar />
      <div className="text-center py-20 text-gray-400">Chargement...</div>
    </div>
  );

  if (!annonce) return null;

  const photos = annonce.photos ? annonce.photos.filter(p => p !== null) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Breadcrumb */}
      <div style={{ background: '#0A0A0A' }} className="px-4 py-3">
        <div className="max-w-7xl mx-auto text-sm text-gray-400">
          <Link to="/" className="hover:text-yellow-400 transition">Accueil</Link>
          <span className="mx-2">›</span>
          <Link to="/annonces" className="hover:text-yellow-400 transition">Annonces</Link>
          <span className="mx-2">›</span>
          <span className="text-white">{annonce.titre}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

          {/* Gauche */}
          <div className="md:col-span-2">

            {/* Photos */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm mb-6 border border-gray-100">
              <div className="overflow-hidden" style={{ maxHeight: '450px' }}>
                {photos.length > 0 ? (
                  photos[photoActive]?.includes('.mp4') || photos[photoActive]?.includes('.mov') ? (
                    <video
                      src={getImageUrl(photos[photoActive])}
                      className="w-full h-full object-cover"
                      controls
                    />
                  ) : (
                    <img
                      src={getImageUrl(photos[photoActive])}
                      alt={annonce.titre}
                      className="w-full object-cover"
                      style={{ maxHeight: '450px' }}
                    />
                  )
                ) : (
                  <div className="h-80 flex items-center justify-center bg-gray-100">
                    <Car size={80} className="text-gray-300" />
                  </div>
                )}
              </div>

              {/* Miniatures */}
              {photos.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto">
                  {photos.map((photo, index) => (
                    <div
                      key={index}
                      onClick={() => setPhotoActive(index)}
                      style={{ borderColor: photoActive === index ? '#F5A623' : 'transparent' }}
                      className="h-16 w-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden cursor-pointer border-2 transition"
                    >
                      {photo?.includes('.mp4') || photo?.includes('.mov') ? (
                        <div className="w-full h-full flex items-center justify-center bg-gray-800">
                          <span className="text-white text-xl">▶</span>
                        </div>
                      ) : (
                        <img
                          src={getImageUrl(photo)}
                          alt={`miniature ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Titre et prix */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 border border-gray-100">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h1 className="text-xl md:text-2xl font-black text-gray-900">{annonce.titre}</h1>
                  {user && user.id === annonce.vendeur_id && (
                    <Link
                      to={`/annonces/${annonce.id}/modifier`}
                      className="inline-flex items-center gap-1 text-xs mt-2 px-3 py-1 rounded-full border border-gray-200 text-gray-500 hover:border-yellow-400 hover:text-yellow-500 transition"
                    >
                      <Pencil size={12} /> Modifier
                    </Link>
                  )}
                </div>
                <button
                  onClick={handleFavori}
                  style={{ color: favoriAjoute ? '#ef4444' : '#9ca3af' }}
                  className="p-2 hover:scale-110 transition"
                >
                  <Heart size={24} fill={favoriAjoute ? 'currentColor' : 'none'} />
                </button>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <p className="text-2xl md:text-3xl font-black text-gray-900">
                  {parseInt(annonce.prix).toLocaleString('fr-FR')} FCFA
                  {annonce.negociable && (
                    <span style={{ color: '#F5A623' }} className="text-sm font-normal ml-2">Négociable</span>
                  )}
                </p>
                <div style={{ color: '#F5A623' }} className="text-sm font-semibold">
                  {annonce.vues} vue(s)
                </div>
              </div>

              {annonce.localisation && (
                <div className="flex items-center gap-2 text-gray-500 mt-3 text-sm">
                  <MapPin size={16} style={{ color: '#F5A623' }} />
                  <span>{annonce.localisation}</span>
                </div>
              )}
                <p className="text-xs text-gray-400 mb-4">
                  Réf. : <span className="font-bold text-gray-600">JJC-{String(annonce.id).padStart(6, '0')}</span>
                </p>
            </div>

            {/* Caractéristiques - ✅ FIX #6: Type véhicule décommenté */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 border border-gray-100">
              <h2 className="font-black text-base md:text-lg mb-4 text-gray-900 flex items-center gap-2">
                <span style={{ color: '#F5A623' }}>|</span> Caractéristiques
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: Calendar, label: 'Année', value: annonce.annee },
                  { icon: Gauge, label: 'Kilométrage', value: `${annonce.kilometrage.toLocaleString()} km` },
                  { icon: Fuel, label: 'Carburant', value: annonce.carburant },
                  { icon: Settings, label: 'Transmission', value: annonce.transmission },
                  { icon: Car, label: 'Type', value: annonce.type_vehicule }
                ].filter(item => item.value).map(({ icon: Icon, label, value }) => (
                  <div key={label} className="text-center p-4 bg-gray-50 rounded-xl">
                    <Icon size={24} className="mx-auto mb-2" style={{ color: '#F5A623' }} />
                    <p className="text-xs text-gray-500 mb-1">{label}</p>
                    <p className="font-bold text-gray-900 capitalize">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            {annonce.description && (
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                <h2 className="font-black text-base md:text-lg mb-4 text-gray-900 flex items-center gap-2">
                  <span style={{ color: '#F5A623' }}>|</span> Description
                </h2>
                <p className="text-gray-600 leading-relaxed">{annonce.description}</p>
              </div>
            )}
          </div>

          {/* Droite */}
          <div>
            {/* Vendeur */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 border border-gray-100">
              <h2 className="font-black text-base md:text-lg mb-4 text-gray-900 flex items-center gap-2">
                <span style={{ color: '#F5A623' }}>|</span> Vendeur
              </h2>
              <div className="flex items-center gap-3 mb-4">
                <div style={{ background: '#F5A623' }} className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-black font-black text-lg">
                    {annonce.prenom?.[0]}{annonce.nom?.[0]}
                  </span>
                </div>
                <div>
                  <Link to={`/vitrine/${annonce.vendeur_id}`} className="font-bold text-gray-900 hover:text-yellow-500 transition">
                    {annonce.prenom} {annonce.nom}
                  </Link>
                  
                  <BadgeVerification niveau={annonce.niveau_verification} size="sm" />
                  <p className="text-sm text-gray-500">{annonce.telephone}</p>
                </div>
              </div>

              {/* Bouton WhatsApp */}
              <a
                href={`https://wa.me/${annonce.telephone.replace(/\s/g, '')}?text=${encodeURIComponent(`Bonjour, je suis intéressé par votre annonce "${annonce.titre}" à ${parseInt(annonce.prix).toLocaleString('fr-FR')} FCFA`)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { contactWhatsapp(annonce.id).catch(() => {}); }}
                style={{ background: '#25D366' }}
                className="w-full flex items-center justify-center gap-2 text-white rounded-xl py-2.5 mb-3 text-sm font-bold hover:opacity-90 transition"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Contacter sur WhatsApp
              </a>

              <Link
                to={`/vitrine/${annonce.vendeur_id}`}
                style={{ borderColor: '#F5A623', color: '#F5A623' }}
                className="w-full block text-center border rounded-xl py-2 text-sm font-semibold hover:bg-yellow-50 transition"
              >
                Voir toutes ses annonces →
              </Link>
            </div>

            {/* Contact */}
            {user && user.id !== annonce.vendeur_id && (
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                <h2 className="font-black text-base md:text-lg mb-4 text-gray-900 flex items-center gap-2">
                  <MessageCircle size={20} style={{ color: '#F5A623' }} />
                  Contacter le vendeur
                </h2>
                {msgEnvoye ? (
                  <div className="bg-green-50 text-green-600 p-4 rounded-xl text-sm text-center font-semibold">
                    ✅ Message envoyé avec succès !
                  </div>
                ) : (
                  <form onSubmit={handleMessage}>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Bonjour, est-ce que ce véhicule est encore disponible ?"
                      rows={4}
                      required
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yellow-400 transition mb-3 resize-none"
                    />
                    <button
                      type="submit"
                      style={{ background: '#F5A623' }}
                      className="w-full text-black py-3 rounded-xl font-bold hover:opacity-90 transition"
                    >
                      Envoyer le message
                    </button>
                  </form>
                )}
              </div>
            )}

            {!user && (
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 text-center">
                <p className="text-gray-500 mb-4 text-sm">Connectez-vous pour contacter le vendeur</p>
                <button
                  onClick={() => navigate('/connexion')}
                  style={{ background: '#F5A623' }}
                  className="w-full text-black py-3 rounded-xl font-bold hover:opacity-90 transition"
                >
                  Se connecter
                </button>
              </div>
            )}
            {/* Pub sidebar */}
            <div className="mt-4">
              <BannierePublicitaire
                emplacement="annonce_sidebar"
                className="w-full rounded-2xl overflow-hidden"
                style={{ minHeight: '250px' }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 pb-6">
        <BannierePublicitaire
          emplacement="detail_annonce_bottom"
          className="h-24"
        />
      </div>
    </div>
  );
};

export default DetailAnnonce;